import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Box, Button, Paper, TextField, Typography} from '@mui/material'
import {DataGrid, GridColDef, GridRowsProp} from '@mui/x-data-grid'
import {ProductSample, SaveInventoryItem, SaveInventoryRequestItem} from '../../utils'
import excludedSound from '../../../assets/sounds/excluded.mp3'
import SaveIcon from '@mui/icons-material/Save'
import {ItemStatus, NotificationSnackbar} from '../../../common/ui'

interface NonSerialStepProps {
    productSamples: ProductSample[]
    nonSerialItems: SaveInventoryItem[]
    saveRequestItem: (requestItem: SaveInventoryRequestItem) => void
    loading: Record<string, boolean>
    setLoading: (loadingState: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void
    qtyChanged: boolean
    setQtyChanged: (value: boolean) => void
}

interface QueuedEdit {
    sku: string
    previousQty: number
    currentQty: number
}

const NonSerialStep = ({
                           productSamples,
                           nonSerialItems,
                           saveRequestItem,
                           loading,
                           setLoading,
                           qtyChanged,
                           setQtyChanged
                       }: NonSerialStepProps) => {
    const [error, setError] = useState<string | null>(null)
    const [quantities, setQuantities] = useState<{[sku: string]: number}>({})
    const [initialQuantities, setInitialQuantities] = useState<{[sku: string]: number}>({})
    const [savedItems, setSavedItems] = useState<{
        sku: string;
        quantity: number,
        showSaveIcon: boolean,
        status: string | undefined,
        disabled: boolean,
        enteredAt: string
    }[]>([])
    const [savedQty, setSavedQty] = useState<number | null>(null)
    const [pendingQty, setPendingQty] = useState<number | null>(null)
    const [activeSku, setActiveSku] = useState<string | null>(null)
    const [queuedEdits, setQueuedEdits] = useState<QueuedEdit[]>([])
    const inputRefs = useRef<{[sku: string]: HTMLInputElement | null}>({})
    const audioPlayer = useRef<HTMLAudioElement | null>(null)

    const playAudio = () => {
        if (audioPlayer.current) {
            audioPlayer.current.play().catch(error => {
                console.error('Error playing audio:', error)
            })
        }
    }

    const sampleNonSerialItems = useMemo(
        () => productSamples.filter((x) => !x.serialized),
        [productSamples]
    )

    const reportNonSerialItems = useMemo(() => {
        // sorting to prevent reordering on save/update
        return nonSerialItems
            .sort((a, b) => {
                if (a.bin === null && b.bin !== null) return 1
                if (a.bin !== null && b.bin === null) return -1

                // both bins non-null
                if (a.bin! < b.bin!) return -1
                if (a.bin! > b.bin!) return 1

                // if bins are equal, sort by sku
                if (a.sku < b.sku) return -1
                if (a.sku > b.sku) return 1

                return 0
            })
    }, [nonSerialItems])

    useEffect(() => {
        const reportMap: {[key: string]: number} = {}
        for (const item of reportNonSerialItems) {
            reportMap[item.sku] = item.quantity
        }

        const initialQuantities: {[key: string]: number} = {}
        for (const item of sampleNonSerialItems) {
            initialQuantities[item.sku] = reportMap[item.sku] || 0
        }

        setQuantities(initialQuantities)
        setInitialQuantities(initialQuantities)

        setSavedItems(reportNonSerialItems.map((item) => {
            const initialQuantity = initialQuantities[item.sku] || 0
            const showSaveIcon = initialQuantity !== item.quantity || !reportMap[item.sku]

            return {
                sku: item.sku,
                quantity: item.quantity,
                showSaveIcon,
                status: item.progress,
                disabled: false,
                enteredAt: item.enteredAt
            }
        }))
    }, [productSamples, reportNonSerialItems])


    const handleQuantityChange = (sku: string, newQuantity: number) => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }
        const sanitizedValue = Math.max(newQuantity, 0)
        setQuantities((prev) => ({...prev, [sku]: sanitizedValue}))
        setQtyChanged(sanitizedValue !== initialQuantities[sku])

        setSavedItems((prev) => prev.map((item) =>
            item.sku === sku
                ? {...item, showSaveIcon: sanitizedValue !== initialQuantities[sku]}
                : item
        ))
    }

    const handleSaveItem = (sku: string) => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }
        const quantity = quantities[sku] ?? 0
        setLoading((prev: {[sku: string]: boolean}) => ({...prev, [sku]: true}))

        if (quantity < 0) {
            setError('Quantity must be greater than or equal to 0.')
            setLoading((prev: {[sku: string]: boolean}) => ({...prev, [sku]: false}))
            return
        }

        try {
            const existingItem = savedItems.find((item) => item.sku === sku)
            if (existingItem) {
                const updatedItem = {...existingItem, quantity}

                setSavedItems((prev) => prev.map((item) => (item.sku === sku ? updatedItem : item)))

                saveRequestItem({
                    op: 'update',
                    sku: sku,
                    quantity,
                    serial: null,
                    bin: null,
                    enteredAt: updatedItem.enteredAt
                })

                setQueuedEdits(prev => {
                    const existingEditIndex = prev.findIndex(item => item.sku === existingItem.sku)
                    if (existingEditIndex !== -1) {
                        const updatedEdits = [...prev]
                        updatedEdits[existingEditIndex] = {
                            sku: existingItem.sku,
                            previousQty: existingItem?.quantity || 0,
                            currentQty: quantity
                        }
                        return updatedEdits
                    } else {
                        return [
                            ...prev,
                            {
                                sku: existingItem.sku,
                                previousQty: existingItem?.quantity || 0,
                                currentQty: quantity
                            }
                        ]
                    }
                })
            } else {
                const newItem = {
                    sku,
                    quantity,
                    showSaveIcon: false,
                    status: '',
                    disabled: false,
                    enteredAt: new Date().toISOString()
                }

                setSavedItems((prev) => [...prev, newItem])

                saveRequestItem({
                    op: 'add',
                    sku: sku,
                    quantity,
                    serial: null,
                    bin: null,
                    enteredAt: newItem.enteredAt
                })
            }
            setInitialQuantities((prev) => ({...prev, [sku]: quantity}))
            setSavedItems((prev) =>
                prev.map((item) =>
                    item.sku === sku
                        ? {...item, showSaveIcon: false, status: '', disabled: false}
                        : {...item, disabled: false}
                )
            )
            setLoading((prev) => ({...prev, [sku]: false}))
            setActiveSku(null)
            setQtyChanged(false)
        } catch (error) {
            console.error(error)
        }
    }

    const handleFocus = (sku: string) => {
        setActiveSku(sku)
    }

    const rows: GridRowsProp = sampleNonSerialItems.map((item) => {
        let reportItem = reportNonSerialItems.find(reportItem => reportItem.sku === item.sku)
        let quantity = quantities[item.sku] || 0

        return {
            id: item.sku,
            sku: item.sku,
            modelNumber: item.modelNumber || '',
            bin: item.bin || '',
            quantity,
            status: reportItem?.progress,
            showSaveIcon:
                item.sku === activeSku &&
                savedItems.some(saved => saved.sku === item.sku && saved.showSaveIcon) ||
                !reportNonSerialItems.find(reportItem => reportItem.sku === item.sku),

            disabled: qtyChanged && item.sku !== activeSku
        }
    })

    const columns: GridColDef[] = [
        {field: 'sku', headerName: 'Item', flex: 1},
        {field: 'modelNumber', headerName: 'Model', flex: 1},
        {field: 'bin', headerName: 'Location', flex: 1},
        {
            field: 'quantity',
            headerName: 'Quantity',
            flex: 1,
            renderCell: (params) => {
                inputRefs.current[params.row.sku] = document.createElement('input')
                let queuedEdit = queuedEdits.find(item => item.sku === params.row.sku)
                return (
                    <TextField
                        inputRef={(ref) => (inputRefs.current[params.row.sku] = ref)}
                        type="number"
                        value={queuedEdit ? queuedEdit.currentQty : params.row.quantity !== undefined ? params.row.quantity : 0}
                        onChange={(e) => handleQuantityChange(params.row.sku, Number(e.target.value))}
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputProps={{inputProps: {min: 0}, readOnly: params.row.disabled}}
                        onFocus={() => handleFocus(params.row.sku)}
                        // onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, params.row.sku, rows.indexOf(params.row))}
                        disabled={params.row.disabled}
                    />
                )
            }
        },
        {
            field: 'status', headerName: 'Status', flex: 1,
            renderCell: (params) => {
                return !params.row.showSaveIcon && (
                    <ItemStatus progress={params.row.status} />
                )
            }
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.5,
            renderCell: (params) => (
                params.row.showSaveIcon && (
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: 'primary',
                                '&:hover': {
                                    backgroundColor: 'primary.dark'
                                }
                            }}
                            onClick={() => handleSaveItem(params.row.sku)}
                            disabled={params.row.disabled}
                        >
                            <SaveIcon sx={{color: 'white'}} />
                        </Button>
                    </Box>
                )
            )
        }
    ]


    useEffect(() => {
        let nonSerialQty = 0
        let nonSerialPendingQty = 0

        for (let item of reportNonSerialItems) {
            let queuedEdit = queuedEdits.find(edit => edit.sku === item.sku)
            if (item.progress) {
                if (queuedEdit) {
                    nonSerialPendingQty += Math.abs(queuedEdit.currentQty - queuedEdit.previousQty)
                    nonSerialQty += item.quantity
                } else {
                    nonSerialPendingQty += item.quantity
                }
                if (item.progress === 'q-delete' || item.progress === 'deleting') {
                    nonSerialQty += item.quantity
                }
            } else {
                if (queuedEdit) {
                    setQueuedEdits(prev => prev.filter(edit => edit.sku !== item.sku))
                }
                nonSerialQty += item.quantity
            }
        }
        setSavedQty(nonSerialQty)
        setPendingQty(nonSerialPendingQty)
    }, [reportNonSerialItems])

    return (
        <Box flex={1} p={2} border={2} borderColor="grey.400" borderRadius={2}>
            <Typography variant="h5">Enter Non-Serials</Typography>
            <Typography variant="subtitle1" color="textSecondary">
                <span style={{fontWeight: 700, color: '#2e7d32'}}>Saved: </span>
                {savedQty === 1 ? `${savedQty} serial` : `${savedQty} serials`},{' '}<span
                style={{fontWeight: 700}}>Pending: </span>
                {pendingQty} quantity
            </Typography>
            <audio ref={audioPlayer} src={excludedSound} />
            {sampleNonSerialItems.length > 0 ? (
                <Paper style={{height: 400}}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowHeight={() => 'auto'}
                        disableRowSelectionOnClick
                    />
                </Paper>
            ) : (
                <Typography variant="h6" color="error" align="center" style={{marginTop: '20px'}}>
                    No non-serial items in the sample.
                </Typography>
            )}

            {error &&
                <NotificationSnackbar
                    open={!!error}
                    message={error}
                    onClose={() => setError(null)}
                    severity="error"
                    autoHideDuration={6000}
                    anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                />
            }
        </Box>
    )
}

export default NonSerialStep
